import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as d3 from 'd3';
import './App.css';

// Import configuration and utilities
import { CONFIG } from './config';
import {
  loadData,
  buildClusterLookup,
  getClustersAtLevel,
  isOutcomeCluster,
  getPrimaryMemberCount,
  getAllDescendantIds,
  getDominantClinicalInterpretation,
  findEdgesForExpandedView,
  getConnectedToFocusedCluster,
  calculateChildCircleLayout,
  getUnderlyingPapers
} from './utils';


// =============================================================================
// VISUALIZATION COMPONENT
// =============================================================================

function KnowledgeGraph({ data }) {
  const svgRef = useRef(null);
  const zoomRef = useRef(null);
  const transformRef = useRef(d3.zoomIdentity); // Store current transform
  const containerRef = useRef(null);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [expandedClusters, setExpandedClusters] = useState(new Set()); // Now tracks any cluster at any depth
  const [phasedOutClusters, setPhasedOutClusters] = useState(new Set()); // Clusters manually faded by user
  const [fadedEdgeOpacity, setFadedEdgeOpacity] = useState(0.06); // Opacity for phased-out edges (0 to 0.2)
  const [focusedOutcome, setFocusedOutcome] = useState(null);
  const [showDetailInfo, setShowDetailInfo] = useState(false); // Whether to show additional info in detail panel
  const [showEdgesInfo, setShowEdgesInfo] = useState(false); // Whether to show attached edges in detail panel
  const [tooltip, setTooltip] = useState(null);
  const [pinnedTooltip, setPinnedTooltip] = useState(null); // Pinned tooltip that stays until closed
  const [showPinnedPapers, setShowPinnedPapers] = useState(false); // Whether papers section is expanded in pinned tooltip
  const [showLegend, setShowLegend] = useState(false);
  const [clusterLookup, setClusterLookup] = useState({});
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [dimensions, setDimensions] = useState({ width: CONFIG.width, height: CONFIG.height });
  const [clusterPositions, setClusterPositions] = useState({}); // Store custom positions: { clusterId: { x, y } }
  const [detailPanelPosition, setDetailPanelPosition] = useState(null); // Custom position for detail panel
  const [contentScale, setContentScale] = useState(1.0); // Scale factor for graph elements
  const [boxScale, setBoxScale] = useState(1.0); // Scale factor for tooltips/panels
  
  // Drag state refs (using refs to avoid re-renders during drag)
  const dragRef = useRef({
    isDragging: false,
    clusterId: null,
    startX: 0,
    startY: 0,
    clusterStartX: 0,
    clusterStartY: 0
  });
  
  // Tooltip drag state ref
  const tooltipDragRef = useRef({
    isDragging: false,
    startMouseX: 0,
    startMouseY: 0,
    startTooltipX: 0,
    startTooltipY: 0
  });
  
  // Detail panel drag state ref
  const detailPanelDragRef = useRef({
    isDragging: false,
    startMouseX: 0,
    startMouseY: 0,
    startPanelX: 0,
    startPanelY: 0
  });
  
  // Fixed at level 1 - navigation is through expansion only
  const currentLevel = CONFIG.initialLevel;
  
  // Build cluster lookup on data load
  useEffect(() => {
    if (data) {
      setClusterLookup(buildClusterLookup(data.hierarchy));
    }
  }, [data]);
  
  // Update dimensions when fullscreen changes
  useEffect(() => {
    const updateDimensions = () => {
      if (document.fullscreenElement || isFullscreen) {
        setDimensions({
          width: window.innerWidth,
          height: window.innerHeight
        });
      } else {
        setDimensions({
          width: CONFIG.width,
          height: CONFIG.height
        });
      }
    };
    
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    document.addEventListener('fullscreenchange', () => {
      if (!document.fullscreenElement) {
        setIsFullscreen(false);
      }
      updateDimensions();
    });
    
    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, [isFullscreen]);
  
  // Handle escape key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        // Browser handles ESC for native fullscreen, but we update state
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);
  
  // Handle ctrl+scroll for scaling (graph vs boxes)
  useEffect(() => {
    const handleWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        
        // Check if scrolling over a tooltip or detail panel
        const isOverBox = e.target.closest('.tooltip') || e.target.closest('.detail-panel');
        
        if (isOverBox) {
          // Scale the boxes (tooltips and panels)
          setBoxScale(prev => Math.min(2.0, Math.max(0.5, prev + delta)));
        } else {
          // Scale the graph elements
          setContentScale(prev => Math.min(2.0, Math.max(0.5, prev + delta)));
        }
      }
    };
    
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel);
    }
  }, []);
  
  const toggleFullscreen = useCallback(async () => {
    if (!isFullscreen) {
      // Enter native fullscreen
      try {
        if (containerRef.current?.requestFullscreen) {
          await containerRef.current.requestFullscreen();
        } else if (containerRef.current?.webkitRequestFullscreen) {
          await containerRef.current.webkitRequestFullscreen(); // Safari
        }
        setIsFullscreen(true);
      } catch (err) {
        console.error('Fullscreen error:', err);
        // Fallback to non-native fullscreen
        setIsFullscreen(true);
      }
    } else {
      // Exit fullscreen
      try {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen(); // Safari
        }
      } catch (err) {
        console.error('Exit fullscreen error:', err);
      }
      setIsFullscreen(false);
    }
  }, [isFullscreen]);
  
  // Tooltip drag handlers
  const handleTooltipDragStart = useCallback((e) => {
    // Only start drag on left mouse button
    if (e.button !== 0) return;
    
    e.preventDefault();
    tooltipDragRef.current = {
      isDragging: true,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startTooltipX: pinnedTooltip?.x || 0,
      startTooltipY: pinnedTooltip?.y || 0
    };
    
    // Add global listeners
    document.addEventListener('mousemove', handleTooltipDragMove);
    document.addEventListener('mouseup', handleTooltipDragEnd);
  }, [pinnedTooltip]);
  
  const handleTooltipDragMove = useCallback((e) => {
    if (!tooltipDragRef.current.isDragging) return;
    
    const dx = e.clientX - tooltipDragRef.current.startMouseX;
    const dy = e.clientY - tooltipDragRef.current.startMouseY;
    
    const newX = tooltipDragRef.current.startTooltipX + dx;
    const newY = tooltipDragRef.current.startTooltipY + dy;
    
    setPinnedTooltip(prev => prev ? {
      ...prev,
      x: newX,
      y: newY
    } : null);
  }, []);
  
  const handleTooltipDragEnd = useCallback(() => {
    tooltipDragRef.current.isDragging = false;
    document.removeEventListener('mousemove', handleTooltipDragMove);
    document.removeEventListener('mouseup', handleTooltipDragEnd);
  }, [handleTooltipDragMove]);
  
  // Detail panel drag handlers
  const handleDetailPanelDragMove = useCallback((e) => {
    if (!detailPanelDragRef.current.isDragging) return;
    
    const dx = e.clientX - detailPanelDragRef.current.startMouseX;
    const dy = e.clientY - detailPanelDragRef.current.startMouseY;
    
    const newX = detailPanelDragRef.current.startPanelX + dx;
    const newY = detailPanelDragRef.current.startPanelY + dy;
    
    setDetailPanelPosition({ x: newX, y: newY });
  }, []);
  
  const handleDetailPanelDragEnd = useCallback(() => {
    detailPanelDragRef.current.isDragging = false;
    document.removeEventListener('mousemove', handleDetailPanelDragMove);
    document.removeEventListener('mouseup', handleDetailPanelDragEnd);
  }, [handleDetailPanelDragMove]);
  
  const handleDetailPanelDragStart = useCallback((e) => {
    if (e.button !== 0) return;
    
    e.preventDefault();
    
    // Get current position from state, or calculate from the actual DOM element
    let startX = detailPanelPosition?.x;
    let startY = detailPanelPosition?.y;
    
    if (startX === undefined || startX === null) {
      // Get actual position from DOM - find the panel element
      const panel = e.target.closest('.detail-panel');
      if (panel) {
        const rect = panel.getBoundingClientRect();
        startX = rect.left;
        startY = rect.top;
      } else {
        // Fallback
        const panelWidth = 280;
        startX = window.innerWidth - 20 - panelWidth;
        startY = 70;
      }
    }
    
    detailPanelDragRef.current = {
      isDragging: true,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startPanelX: startX,
      startPanelY: startY
    };
    
    document.addEventListener('mousemove', handleDetailPanelDragMove);
    document.addEventListener('mouseup', handleDetailPanelDragEnd);
  }, [detailPanelPosition, handleDetailPanelDragMove, handleDetailPanelDragEnd]);
  
  const toggleExpanded = useCallback((clusterId) => {
    setExpandedClusters(prev => {
      const next = new Set(prev);
      if (next.has(clusterId)) {
        // Collapsing - also collapse all descendants
        const cluster = clusterLookup[clusterId];
        const removeDescendants = (node) => {
          next.delete(node.cluster_id);
          if (node.children) {
            node.children.forEach(removeDescendants);
          }
        };
        if (cluster) {
          removeDescendants(cluster);
        } else {
          next.delete(clusterId);
        }
      } else {
        next.add(clusterId);
      }
      return next;
    });
  }, [clusterLookup]);
  
  const handleClusterClick = useCallback((cluster, isChild = false) => {
    const clusterId = cluster.cluster_id;
    
    // Left-click moves RIGHT through states: phased-out -> normal -> selected -> expanded
    // State transitions:
    // 1. If phased out -> make normal (un-phase)
    // 2. If normal (not selected) -> select
    // 3. If selected but not expanded -> expand (if has children)
    // 4. If selected and expanded -> do nothing (already at rightmost state)
    
    if (phasedOutClusters.has(clusterId)) {
      // Un-phase the cluster
      setPhasedOutClusters(prev => {
        const next = new Set(prev);
        next.delete(clusterId);
        return next;
      });
      return;
    }
    
    const isCurrentlySelected = selectedCluster?.cluster_id === clusterId;
    const isCurrentlyExpanded = expandedClusters.has(clusterId);
    const hasChildren = cluster.children && cluster.children.length > 0;
    
    if (!isCurrentlySelected) {
      // Normal -> Selected
      setSelectedCluster(cluster);
      setShowDetailInfo(false); // Reset additional info when selecting new cluster
      setShowEdgesInfo(false); // Reset edges info when selecting new cluster
      setDetailPanelPosition(null); // Reset position when selecting new cluster
    } else if (!isCurrentlyExpanded && hasChildren) {
      // Selected -> Expanded
      toggleExpanded(clusterId);
    }
    // If already selected and expanded (or no children), do nothing
  }, [selectedCluster, expandedClusters, phasedOutClusters, toggleExpanded]);
  
  const handleClusterRightClick = useCallback((cluster, event) => {
    event.preventDefault(); // Prevent browser context menu
    
    const clusterId = cluster.cluster_id;
    
    // Right-click moves LEFT through states: expanded -> selected -> normal -> phased-out
    // State transitions:
    // 1. If expanded -> collapse (stay selected)
    // 2. If selected but not expanded -> deselect
    // 3. If normal (not selected) -> phase out
    // 4. If phased out -> do nothing (already at leftmost state)
    
    if (phasedOutClusters.has(clusterId)) {
      // Already phased out, do nothing
      return;
    }
    
    const isCurrentlySelected = selectedCluster?.cluster_id === clusterId;
    const isCurrentlyExpanded = expandedClusters.has(clusterId);
    
    if (isCurrentlyExpanded) {
      // Expanded -> Collapse (stay selected)
      toggleExpanded(clusterId);
    } else if (isCurrentlySelected) {
      // Selected -> Deselect
      setSelectedCluster(null);
    } else {
      // Normal -> Phase out
      setPhasedOutClusters(prev => {
        const next = new Set(prev);
        next.add(clusterId);
        return next;
      });
    }
  }, [selectedCluster, expandedClusters, phasedOutClusters, toggleExpanded]);
  
  const handleFocusOutcome = useCallback((clusterId) => {
    setFocusedOutcome(prev => prev === clusterId ? null : clusterId);
  }, []);
  
  const renderGraph = useCallback(() => {
    if (!data || !svgRef.current) return;
    
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    
    const { width, height } = dimensions;
    const centerX = width / 2;
    const centerY = height / 2;
    
    // Create main group for zoom/pan
    const mainGroup = svg.append('g').attr('class', 'main-group');
    
    // Setup zoom - filter out ctrl+scroll (used for content scaling)
    const zoom = d3.zoom()
      .scaleExtent([0.3, 3])
      .filter((event) => {
        // Allow all non-wheel events (drag, etc.)
        if (event.type !== 'wheel') return true;
        // For wheel events, only allow if ctrl/meta is NOT pressed
        return !event.ctrlKey && !event.metaKey;
      })
      .on('zoom', (event) => {
        mainGroup.attr('transform', event.transform);
        transformRef.current = event.transform; // Save transform
      });
    
    svg.call(zoom);
    zoomRef.current = zoom;
    
    // Restore previous transform (without animation to avoid jarring)
    if (transformRef.current !== d3.zoomIdentity) {
      mainGroup.attr('transform', transformRef.current);
      svg.call(zoom.transform, transformRef.current);
    }
    
    // Get clusters at current level
    const clusters = getClustersAtLevel(data.hierarchy, currentLevel);
    
    // Get edges that account for expanded clusters
    const displayEdges = findEdgesForExpandedView(
      data.clusterEdges, 
      expandedClusters, 
      currentLevel,
      clusterLookup
    );
    
    // Determine which clusters are connected to focused outcome
    const connectedToFocus = focusedOutcome 
      ? getConnectedToFocusedCluster(data.clusterEdges.cluster_edges, focusedOutcome, clusterLookup)
      : null;
    
    // Separate outcome and intervention clusters
    const outcomeClusters = clusters.filter(c => isOutcomeCluster(c, CONFIG));
    const interventionClusters = clusters.filter(c => !isOutcomeCluster(c, CONFIG));
    
    // Calculate cluster sizes - apply contentScale to radii
    const memberCounts = clusters.map(c => getPrimaryMemberCount(c));
    const maxMembers = Math.max(...memberCounts, 1);
    
    const radiusScale = d3.scaleSqrt()
      .domain([0, maxMembers])
      .range([CONFIG.minClusterRadius * contentScale, CONFIG.maxClusterRadius * contentScale]);
    
    // Position clusters
    const nodes = [];
    
    // Position intervention clusters in left semicircle
    const interventionArcStart = Math.PI * 0.55;
    const interventionArcEnd = Math.PI * 1.45;
    const interventionRadius = Math.min(width, height) * 0.35;
    
    interventionClusters.forEach((cluster, i) => {
      const angle = interventionArcStart + 
        (interventionArcEnd - interventionArcStart) * (i / Math.max(interventionClusters.length - 1, 1));
      
      const baseRadius = radiusScale(getPrimaryMemberCount(cluster));
      const isExpanded = expandedClusters.has(cluster.cluster_id);
      const radius = isExpanded ? baseRadius * CONFIG.expandedRadiusMultiplier : baseRadius;
      
      // Check if this cluster or any of its descendants are connected to focus
      const clusterDescendants = getAllDescendantIds(cluster);
      const isConnectedToFocus = connectedToFocus 
        ? [...clusterDescendants].some(id => connectedToFocus.has(id))
        : true;
      
      // Use custom position if available, otherwise default layout
      const defaultX = centerX + Math.cos(angle) * interventionRadius - 150;
      const defaultY = centerY + Math.sin(angle) * interventionRadius * 0.85;
      const customPos = clusterPositions[cluster.cluster_id];
      
      nodes.push({
        ...cluster,
        x: customPos ? customPos.x : defaultX,
        y: customPos ? customPos.y : defaultY,
        radius,
        baseRadius,
        isOutcome: false,
        isExpanded,
        isSelected: selectedCluster?.cluster_id === cluster.cluster_id,
        isPhasedOut: phasedOutClusters.has(cluster.cluster_id),
        isFaded: phasedOutClusters.has(cluster.cluster_id) || (connectedToFocus && !isConnectedToFocus)
      });
    });
    
    // Position outcome clusters on the right
    const outcomeX = width - 250;
    const outcomeSpacing = height / (outcomeClusters.length + 1);
    
    outcomeClusters.forEach((cluster, i) => {
      const baseRadius = radiusScale(getPrimaryMemberCount(cluster));
      const isExpanded = expandedClusters.has(cluster.cluster_id);
      const radius = isExpanded ? baseRadius * CONFIG.expandedRadiusMultiplier : baseRadius;
      
      // Check if this cluster or any descendant is the focused one
      const clusterDescendants = getAllDescendantIds(cluster);
      const containsFocused = focusedOutcome && clusterDescendants.has(focusedOutcome);
      const isFocusedItself = focusedOutcome === cluster.cluster_id;
      
      // Use custom position if available, otherwise default layout
      const defaultX = outcomeX;
      const defaultY = outcomeSpacing * (i + 1);
      const customPos = clusterPositions[cluster.cluster_id];
      
      nodes.push({
        ...cluster,
        x: customPos ? customPos.x : defaultX,
        y: customPos ? customPos.y : defaultY,
        radius,
        baseRadius,
        isOutcome: true,
        isExpanded,
        isSelected: selectedCluster?.cluster_id === cluster.cluster_id,
        isFocused: isFocusedItself || containsFocused,
        isPhasedOut: phasedOutClusters.has(cluster.cluster_id),
        isFaded: phasedOutClusters.has(cluster.cluster_id) // Outcomes can now be phased out too
      });
    });
    
    // Create node lookup
    const nodeById = {};
    nodes.forEach(n => { nodeById[n.cluster_id] = n; });
    
    // Helper to get edge endpoint position - handles nested expanded clusters
    const getEdgePoint = (clusterId) => {
      // Find the base-level node that contains this cluster
      const cluster = clusterLookup[clusterId];
      if (!cluster) return null;

      // Find the base-level ancestor
      let baseLevelAncestor = cluster;
      while (baseLevelAncestor && baseLevelAncestor.level > currentLevel) {
        baseLevelAncestor = baseLevelAncestor.parent;
      }

      if (!baseLevelAncestor) return null;

      const baseNode = nodeById[baseLevelAncestor.cluster_id];
      if (!baseNode) return null;

      // If the cluster is the base level itself, return its center
      if (cluster.cluster_id === baseLevelAncestor.cluster_id) {
        return { x: baseNode.x, y: baseNode.y };
      }

      // Otherwise, trace down through expansions to find position
      const tracePosition = (parentCluster, parentX, parentY, parentRadius, targetId) => {
        if (!expandedClusters.has(parentCluster.cluster_id)) {
          // Not expanded - return parent center
          return { x: parentX, y: parentY };
        }

        if (!parentCluster.children) {
          return { x: parentX, y: parentY };
        }

        // Find which child contains our target
        const { childRadii, positions } = calculateChildCircleLayout(parentRadius, parentCluster.children, contentScale);

        for (let i = 0; i < parentCluster.children.length; i++) {
          const child = parentCluster.children[i];
          const childPos = positions[i];
          const childRadius = childRadii[i];
          const isChildExpanded = expandedClusters.has(child.cluster_id);
          const actualRadius = isChildExpanded ? childRadius * CONFIG.expandedRadiusMultiplier : childRadius;

          const childAbsX = parentX + childPos.x;
          const childAbsY = parentY + childPos.y;

          if (child.cluster_id === targetId) {
            return { x: childAbsX, y: childAbsY };
          }

          // Check if target is a descendant of this child
          const isDescendant = (node, tid) => {
            if (node.cluster_id === tid) return true;
            if (node.children) {
              return node.children.some(c => isDescendant(c, tid));
            }
            return false;
          };

          if (isDescendant(child, targetId)) {
            // Recurse into this child
            return tracePosition(child, childAbsX, childAbsY, actualRadius, targetId);
          }
        }

        // Target not found in children, return parent center
        return { x: parentX, y: parentY };
      };

      return tracePosition(baseLevelAncestor, baseNode.x, baseNode.y, baseNode.radius, clusterId);
    };

    // Process edges - handle recursive expansion
    const edgeData = [];
    
    displayEdges.forEach(edge => {
      const sourceClusterId = edge.displaySource;
      const targetClusterId = edge.displayTarget;
      
      const sourcePoint = getEdgePoint(sourceClusterId);
      const targetPoint = getEdgePoint(targetClusterId);
      
      if (!sourcePoint || !targetPoint) return;
      
      const clinical = getDominantClinicalInterpretation(edge.histograms);
      
      // Check if edge should be faded based on focus or phased-out clusters
      let isFaded = false;
      
      // Check if either endpoint is phased out
      const edgeSourceId = edge.source_cluster;
      const edgeTargetId = edge.target_cluster;
      
      // Get all ancestors of source and target to check if any ancestor is phased out
      const getAncestorIds = (clusterId) => {
        const ancestors = new Set([clusterId]);
        let current = clusterLookup[clusterId];
        while (current && current.parent) {
          ancestors.add(current.parent.cluster_id);
          current = current.parent;
        }
        return ancestors;
      };
      
      const sourceAncestors = getAncestorIds(edgeSourceId);
      const targetAncestors = getAncestorIds(edgeTargetId);
      
      // Edge is faded if any ancestor of source or target is phased out
      const sourceIsPhasedOut = [...sourceAncestors].some(id => phasedOutClusters.has(id));
      const targetIsPhasedOut = [...targetAncestors].some(id => phasedOutClusters.has(id));
      
      if (sourceIsPhasedOut || targetIsPhasedOut) {
        isFaded = true;
      }
      
      // Also check focus-based fading
      if (!isFaded && connectedToFocus) {
        // Check if this edge directly involves the focused outcome or its family
        const focusedCluster = clusterLookup[focusedOutcome];
        const focusedFamily = new Set([focusedOutcome]);
        
        // Add ancestors of focused
        let current = focusedCluster;
        while (current && current.parent) {
          focusedFamily.add(current.parent.cluster_id);
          current = current.parent;
        }
        
        // Add descendants of focused
        if (focusedCluster) {
          const addDescendants = (node) => {
            if (node.children) {
              node.children.forEach(child => {
                focusedFamily.add(child.cluster_id);
                addDescendants(child);
              });
            }
          };
          addDescendants(focusedCluster);
        }
        
        // Edge is visible only if one end is in the focused family
        const edgeConnectsToFocused = focusedFamily.has(edgeSourceId) || focusedFamily.has(edgeTargetId);
        isFaded = !edgeConnectsToFocused;
      }
      
      edgeData.push({
        ...edge,
        sourcePoint,
        targetPoint,
        clinicalInterpretation: clinical,
        color: CONFIG.clinicalColors[clinical] || CONFIG.clinicalColors.unknown,
        isFaded
      });
    });
    
    // Draw edges
    const edgeGroup = mainGroup.append('g').attr('class', 'edges');
    
    // Calculate source and target points for all edges first (already have them)
    const edgesWithPoints = edgeData;
    
    // Group edges by their COMBINED source+target - edges with identical visual paths need spreading
    const pathGroups = {};
    edgesWithPoints.forEach((edge, idx) => {
      const pathKey = `${Math.round(edge.sourcePoint.x)},${Math.round(edge.sourcePoint.y)}->${Math.round(edge.targetPoint.x)},${Math.round(edge.targetPoint.y)}`;
      if (!pathGroups[pathKey]) {
        pathGroups[pathKey] = [];
      }
      pathGroups[pathKey].push({ ...edge, originalIndex: idx });
    });
    
    // Calculate offsets for edges sharing the same path
    const edgesWithOffsets = edgesWithPoints.map((edge, idx) => {
      const pathKey = `${Math.round(edge.sourcePoint.x)},${Math.round(edge.sourcePoint.y)}->${Math.round(edge.targetPoint.x)},${Math.round(edge.targetPoint.y)}`;
      const pathGroup = pathGroups[pathKey];
      
      // Sort group consistently by original edge IDs
      pathGroup.sort((a, b) => 
        (a.source_cluster || '').localeCompare(b.source_cluster || '') ||
        (a.target_cluster || '').localeCompare(b.target_cluster || '') ||
        (a.cluster_edge_id || '').localeCompare(b.cluster_edge_id || '')
      );
      
      // Find this edge's index in the group
      const pathIdx = pathGroup.findIndex(e => e.originalIndex === idx);
      
      // Calculate offset for this edge within its path group
      const spreadPerEdge = 8;
      let pathOffset = 0;
      if (pathGroup.length > 1) {
        const totalSpread = (pathGroup.length - 1) * spreadPerEdge;
        pathOffset = (pathIdx * spreadPerEdge) - (totalSpread / 2);
      }
      
      return { ...edge, pathOffset, pathGroupSize: pathGroup.length };
    });
    
    // Sort so non-faded edges render on top
    edgesWithOffsets.sort((a, b) => (b.isFaded ? 0 : 1) - (a.isFaded ? 0 : 1));
    
    // Determine if we're in focus mode (to boost visible edge prominence)
    const inFocusMode = focusedOutcome !== null;
    
    edgeGroup.selectAll('path.edge-path')
      .data(edgesWithOffsets)
      .enter()
      .append('path')
      .attr('class', 'edge-path')
      .attr('d', d => {
        const { sourcePoint, targetPoint, pathOffset } = d;
        
        const dx = targetPoint.x - sourcePoint.x;
        const dy = targetPoint.y - sourcePoint.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        
        if (len === 0) return `M ${sourcePoint.x} ${sourcePoint.y} L ${targetPoint.x} ${targetPoint.y}`;
        
        // Perpendicular unit vector for offset
        const perpX = -dy / len;
        const perpY = dx / len;
        
        // Start and end at the actual points (no offset at endpoints)
        const sx = sourcePoint.x;
        const sy = sourcePoint.y;
        const tx = targetPoint.x;
        const ty = targetPoint.y;
        
        // Midpoint offset creates the spread in the middle
        const mx = (sourcePoint.x + targetPoint.x) / 2 + perpX * pathOffset;
        const my = (sourcePoint.y + targetPoint.y) / 2 + perpY * pathOffset;
        
        // Add slight curve perpendicular to the line
        const curveAmount = Math.min(30, len * 0.08);
        const cx = mx - perpX * curveAmount;
        const cy = my - perpY * curveAmount;
        
        return `M ${sx} ${sy} Q ${cx} ${cy} ${tx} ${ty}`;
      })
      .attr('stroke', d => d.color)
      .attr('stroke-width', d => {
        const baseWidth = Math.max(1.5, Math.min(5, d.num_edges / 4)) * contentScale;
        // Boost width for non-faded edges in focus mode
        return (inFocusMode && !d.isFaded) ? baseWidth * 1.5 : baseWidth;
      })
      .attr('fill', 'none')
      .attr('opacity', d => {
        if (d.isFaded) return fadedEdgeOpacity;
        // Boost opacity for focused edges
        return inFocusMode ? 0.85 : CONFIG.edgeOpacity;
      })
      .style('cursor', d => d.isFaded ? 'default' : 'pointer')
      .on('mouseover', (event, d) => {
        if (d.isFaded) return;
        if (pinnedTooltip) return; // Don't show hover tooltip if one is pinned
        setTooltip({
          x: event.clientX,
          y: event.clientY,
          content: d
        });
        d3.select(event.target)
          .attr('opacity', 1)
          .attr('stroke-width', d => Math.max(3, Math.min(8, d.num_edges / 2)) * contentScale);
      })
      .on('mouseout', (event, d) => {
        if (!pinnedTooltip) {
          setTooltip(null);
        }
        const baseWidth = Math.max(1.5, Math.min(5, d.num_edges / 4)) * contentScale;
        d3.select(event.target)
          .attr('opacity', d.isFaded ? fadedEdgeOpacity : (inFocusMode ? 0.85 : CONFIG.edgeOpacity))
          .attr('stroke-width', (inFocusMode && !d.isFaded) ? baseWidth * 1.5 : baseWidth);
      })
      .on('click', (event, d) => {
        if (d.isFaded) return;
        event.stopPropagation();
        // Pin the tooltip at a fixed position
        setPinnedTooltip({
          x: event.clientX,
          y: event.clientY,
          content: d
        });
        setShowPinnedPapers(false); // Reset papers section
        setTooltip(null); // Clear hover tooltip
      });
    
    // Add flow animation overlay (only for non-faded edges)
    edgeGroup.selectAll('path.flow-overlay')
      .data(edgesWithOffsets.filter(d => !d.isFaded))
      .enter()
      .append('path')
      .attr('class', 'flow-overlay')
      .attr('d', d => {
        const { sourcePoint, targetPoint, pathOffset } = d;
        
        const dx = targetPoint.x - sourcePoint.x;
        const dy = targetPoint.y - sourcePoint.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        
        if (len === 0) return `M ${sourcePoint.x} ${sourcePoint.y} L ${targetPoint.x} ${targetPoint.y}`;
        
        // Perpendicular unit vector for offset
        const perpX = -dy / len;
        const perpY = dx / len;
        
        // Start and end at actual points
        const sx = sourcePoint.x;
        const sy = sourcePoint.y;
        const tx = targetPoint.x;
        const ty = targetPoint.y;
        
        // Spread in the middle
        const mx = (sourcePoint.x + targetPoint.x) / 2 + perpX * pathOffset;
        const my = (sourcePoint.y + targetPoint.y) / 2 + perpY * pathOffset;
        
        const curveAmount = Math.min(30, len * 0.08);
        const cx = mx - perpX * curveAmount;
        const cy = my - perpY * curveAmount;
        
        return `M ${sx} ${sy} Q ${cx} ${cy} ${tx} ${ty}`;
      })
      .attr('stroke', 'white')
      .attr('stroke-width', 2 * contentScale)
      .attr('stroke-dasharray', `${8 * contentScale} ${16 * contentScale}`)
      .attr('fill', 'none')
      .attr('opacity', inFocusMode ? 0.8 : 0.6)
      .style('pointer-events', 'none')
      .each(function() {
        // Use Web Animations API for properly scaled animation
        const dashCycle = 24 * contentScale;
        this.animate(
          [
            { strokeDashoffset: dashCycle },
            { strokeDashoffset: 0 }
          ],
          {
            duration: 1500,
            iterations: Infinity,
            easing: 'linear'
          }
        );
      });
    
    // Draw nodes
    const nodeGroup = mainGroup.append('g').attr('class', 'nodes');
    
    // Recursive function to render child circles at any depth
    const renderChildCircles = (parentGroup, parentNode, children, parentRadius, isOutcome, depth) => {
      if (!children || children.length === 0) return;
      
      const { childRadii, positions } = calculateChildCircleLayout(parentRadius, children, contentScale);
      
      children.forEach((child, i) => {
        if (i >= positions.length) return;
        
        const pos = positions[i];
        const baseChildRadius = childRadii[i];
        const isChildExpanded = expandedClusters.has(child.cluster_id);
        const isChildPhasedOut = phasedOutClusters.has(child.cluster_id);
        const isChildSelected = selectedCluster?.cluster_id === child.cluster_id;
        const childRadius = isChildExpanded ? baseChildRadius * CONFIG.expandedRadiusMultiplier : baseChildRadius;
        
        // Create a group for this child (allows nested transforms)
        const childGroup = parentGroup.append('g')
          .attr('class', 'child-group')
          .attr('transform', `translate(${pos.x}, ${pos.y})`)
          .attr('opacity', isChildPhasedOut ? 0.2 : 1); // Apply fade to entire group
        
        // Child circle
        childGroup.append('circle')
          .attr('class', isChildExpanded ? 'child-circle expanded' : 'child-circle')
          .attr('cx', 0)
          .attr('cy', 0)
          .attr('r', childRadius)
          .attr('fill', () => {
            if (isChildExpanded) return isOutcome ? '#fef9e6' : '#f0f7ff';
            return isOutcome ? '#fef3c7' : '#dbeafe';
          })
          .attr('stroke', () => {
            if (isChildSelected) return isOutcome ? '#d97706' : '#1d4ed8';
            return isOutcome ? '#f59e0b' : '#3b82f6';
          })
          .attr('stroke-width', (isChildSelected ? 2.5 : 1.5) * contentScale)
          .attr('opacity', isChildExpanded ? 0.5 : 1)
          .style('cursor', 'pointer')
          .on('click', (event) => {
            event.stopPropagation();
            handleClusterClick({ ...child, isOutcome: isOutcome });
          })
          .on('contextmenu', (event) => {
            event.stopPropagation();
            handleClusterRightClick({ ...child, isOutcome: isOutcome }, event);
          })
          .on('mouseover', (event) => {
            event.stopPropagation();
            setTooltip({
              x: event.clientX,
              y: event.clientY,
              content: {
                isChildTooltip: true,
                name: child.name,
                description: child.description,
                memberCount: getPrimaryMemberCount(child),
                hasChildren: child.children && child.children.length > 0
              }
            });
          })
          .on('mouseout', (event) => {
            event.stopPropagation();
            setTooltip(null);
          });
        
        if (isChildExpanded && child.children && child.children.length > 0) {
          // Recursively render grandchildren
          renderChildCircles(childGroup, child, child.children, childRadius, isOutcome, depth + 1);
          
          // Show abbreviated name at top of expanded child
          const fontSize = Math.max(7 * contentScale, Math.min(10 * contentScale, childRadius * 0.15));
          childGroup.append('text')
            .attr('class', 'child-label-expanded')
            .attr('text-anchor', 'middle')
            .attr('y', -childRadius + fontSize + 2 * contentScale)
            .attr('font-size', fontSize)
            .attr('fill', '#64748b')
            .attr('font-weight', '500')
            .style('pointer-events', 'none')
            .text(child.name.length > 15 ? child.name.substring(0, 13) + '...' : child.name);
        } else {
          // Child number label
          const fontSize = Math.max(8 * contentScale, Math.min(14 * contentScale, childRadius * 0.6));
          childGroup.append('text')
            .attr('class', 'child-label')
            .attr('x', 0)
            .attr('y', 0)
            .attr('text-anchor', 'middle')
            .attr('dy', '0.35em')
            .attr('font-size', fontSize)
            .attr('font-weight', '600')
            .attr('fill', '#374151')
            .style('pointer-events', 'none')
            .text(i + 1);
        }
      });
    };
    
    const nodeElements = nodeGroup.selectAll('g.node')
      .data(nodes)
      .enter()
      .append('g')
      .attr('class', 'node')
      .attr('transform', d => `translate(${d.x}, ${d.y})`)
      .style('cursor', 'pointer')
      .attr('opacity', d => d.isFaded ? 0.2 : 1);
    
    // Node circles
    nodeElements.append('circle')
      .attr('class', 'node-circle')
      .attr('r', d => d.radius)
      .attr('fill', d => {
        if (d.isExpanded) return d.isOutcome ? '#fef9e6' : '#f0f7ff';
        return d.isOutcome ? '#fef3c7' : '#dbeafe';
      })
      .attr('stroke', d => {
        if (d.isFocused) return '#b45309';
        if (d.isSelected) return d.isOutcome ? '#d97706' : '#1d4ed8'; // Darker shade when selected
        return d.isOutcome ? '#f59e0b' : '#3b82f6';
      })
      .attr('stroke-width', d => {
        const base = d.isFocused ? 4 : (d.isSelected ? 3 : 2);
        return base * contentScale;
      })
      .attr('opacity', d => d.isExpanded ? 0.4 : 1)
      .on('mousedown', (event, d) => {
        // Ctrl+Click starts drag
        if (event.ctrlKey || event.metaKey) {
          event.stopPropagation();
          event.preventDefault();
          
          // Get mouse position in SVG coordinates
          const transform = transformRef.current;
          const mouseX = (event.offsetX - transform.x) / transform.k;
          const mouseY = (event.offsetY - transform.y) / transform.k;
          
          dragRef.current = {
            isDragging: true,
            clusterId: d.cluster_id,
            startX: mouseX,
            startY: mouseY,
            clusterStartX: d.x,
            clusterStartY: d.y
          };
          
          // Change cursor
          svg.style('cursor', 'grabbing');
        }
      })
      .on('click', (event, d) => {
        // Only handle click if not dragging
        if (!dragRef.current.isDragging) {
          event.stopPropagation();
          handleClusterClick(d);
        }
      })
      .on('contextmenu', (event, d) => {
        // Right-click handler
        event.stopPropagation();
        handleClusterRightClick(d, event);
      });
    
    // Global mouse handlers for drag
    svg.on('mousemove.drag', (event) => {
      if (!dragRef.current.isDragging) return;
      
      event.preventDefault();
      
      // Get mouse position in SVG coordinates
      const transform = transformRef.current;
      const mouseX = (event.offsetX - transform.x) / transform.k;
      const mouseY = (event.offsetY - transform.y) / transform.k;
      
      // Calculate delta
      const dx = mouseX - dragRef.current.startX;
      const dy = mouseY - dragRef.current.startY;
      
      // Update the node position visually (real-time feedback)
      const newX = dragRef.current.clusterStartX + dx;
      const newY = dragRef.current.clusterStartY + dy;
      
      // Find and move the node group
      nodeGroup.selectAll('g.node')
        .filter(d => d.cluster_id === dragRef.current.clusterId)
        .attr('transform', `translate(${newX}, ${newY})`);
      
      // Update edges in real-time
      // We need to recalculate edge positions for this cluster
      const clusterId = dragRef.current.clusterId;
      
      edgeGroup.selectAll('path.edge-path')
        .attr('d', function(d) {
          let { sourcePoint, targetPoint, pathOffset } = d;
          
          // Update source or target if it's the dragged cluster
          if (d.displaySource === clusterId) {
            sourcePoint = { x: newX, y: newY };
          }
          if (d.displayTarget === clusterId) {
            targetPoint = { x: newX, y: newY };
          }
          
          const dx = targetPoint.x - sourcePoint.x;
          const dy = targetPoint.y - sourcePoint.y;
          const len = Math.sqrt(dx * dx + dy * dy);
          
          if (len === 0) return `M ${sourcePoint.x} ${sourcePoint.y} L ${targetPoint.x} ${targetPoint.y}`;
          
          const perpX = -dy / len;
          const perpY = dx / len;
          
          const sx = sourcePoint.x;
          const sy = sourcePoint.y;
          const tx = targetPoint.x;
          const ty = targetPoint.y;
          
          const mx = (sourcePoint.x + targetPoint.x) / 2 + perpX * pathOffset;
          const my = (sourcePoint.y + targetPoint.y) / 2 + perpY * pathOffset;
          
          const curveAmount = Math.min(30, len * 0.08);
          const cx = mx - perpX * curveAmount;
          const cy = my - perpY * curveAmount;
          
          return `M ${sx} ${sy} Q ${cx} ${cy} ${tx} ${ty}`;
        });
      
      // Update flow overlays too
      edgeGroup.selectAll('path.flow-overlay')
        .attr('d', function(d) {
          let { sourcePoint, targetPoint, pathOffset } = d;
          
          if (d.displaySource === clusterId) {
            sourcePoint = { x: newX, y: newY };
          }
          if (d.displayTarget === clusterId) {
            targetPoint = { x: newX, y: newY };
          }
          
          const dx = targetPoint.x - sourcePoint.x;
          const dy = targetPoint.y - sourcePoint.y;
          const len = Math.sqrt(dx * dx + dy * dy);
          
          if (len === 0) return `M ${sourcePoint.x} ${sourcePoint.y} L ${targetPoint.x} ${targetPoint.y}`;
          
          const perpX = -dy / len;
          const perpY = dx / len;
          
          const sx = sourcePoint.x;
          const sy = sourcePoint.y;
          const tx = targetPoint.x;
          const ty = targetPoint.y;
          
          const mx = (sourcePoint.x + targetPoint.x) / 2 + perpX * pathOffset;
          const my = (sourcePoint.y + targetPoint.y) / 2 + perpY * pathOffset;
          
          const curveAmount = Math.min(30, len * 0.08);
          const cx = mx - perpX * curveAmount;
          const cy = my - perpY * curveAmount;
          
          return `M ${sx} ${sy} Q ${cx} ${cy} ${tx} ${ty}`;
        });
    });
    
    svg.on('mouseup.drag', (event) => {
      if (!dragRef.current.isDragging) return;
      
      // Get final position
      const transform = transformRef.current;
      const mouseX = (event.offsetX - transform.x) / transform.k;
      const mouseY = (event.offsetY - transform.y) / transform.k;
      
      const dx = mouseX - dragRef.current.startX;
      const dy = mouseY - dragRef.current.startY;
      
      const newX = dragRef.current.clusterStartX + dx;
      const newY = dragRef.current.clusterStartY + dy;
      
      // Save position to state (persists across re-renders)
      const clusterId = dragRef.current.clusterId;
      setClusterPositions(prev => ({
        ...prev,
        [clusterId]: { x: newX, y: newY }
      }));
      
      // Reset drag state
      dragRef.current = {
        isDragging: false,
        clusterId: null,
        startX: 0,
        startY: 0,
        clusterStartX: 0,
        clusterStartY: 0
      };
      
      // Reset cursor
      svg.style('cursor', null);
    });
    
    svg.on('mouseleave.drag', () => {
      // Cancel drag if mouse leaves SVG
      if (dragRef.current.isDragging) {
        dragRef.current.isDragging = false;
        svg.style('cursor', null);
      }
    });
    
    // Node labels (only for non-expanded or show abbreviated)
    nodeElements.each(function(d) {
      const group = d3.select(this);
      
      if (d.isExpanded) {
        // Show abbreviated name at top
        group.append('text')
          .attr('class', 'node-label-expanded')
          .attr('text-anchor', 'middle')
          .attr('y', -d.radius + 15 * contentScale)
          .attr('font-size', 11 * contentScale)
          .attr('fill', '#64748b')
          .attr('font-weight', '500')
          .text(d.name.length > 20 ? d.name.substring(0, 18) + '...' : d.name);
        
        // Draw child circles inside using optimized packing
        if (d.children && d.children.length > 0) {
          renderChildCircles(group, d, d.children, d.radius, d.isOutcome, 1);
        }
      } else {
        // Regular label - fontSize already scales with radius which scales with contentScale
        const fontSize = Math.max(10 * contentScale, Math.min(14 * contentScale, d.radius / 3));
        const maxLen = Math.floor(d.radius / (4 * contentScale));
        const words = d.name.split(/\s+/);
        
        const text = group.append('text')
          .attr('class', 'node-label')
          .attr('text-anchor', 'middle')
          .attr('font-size', fontSize)
          .attr('fill', '#1e293b');
        
        if (words.length > 1 && d.name.length > maxLen) {
          const lineHeight = fontSize + 2 * contentScale;
          const lines = [];
          let currentLine = '';
          
          words.forEach(word => {
            if (currentLine.length + word.length < d.radius / (5 * contentScale)) {
              currentLine += (currentLine ? ' ' : '') + word;
            } else {
              if (currentLine) lines.push(currentLine);
              currentLine = word;
            }
          });
          if (currentLine) lines.push(currentLine);
          
          const showLines = lines.slice(0, 3);
          
          showLines.forEach((line, i) => {
            text.append('tspan')
              .attr('x', 0)
              .attr('dy', i === 0 ? `${-(showLines.length - 1) * lineHeight / 2}px` : `${lineHeight}px`)
              .text(line);
          });
        } else {
          text.attr('dy', '0.35em')
            .text(d.name.length > maxLen ? d.name.substring(0, maxLen - 2) + '...' : d.name);
        }
      }
    });
    
    // Note: removed svg.on('click') to deselect - this interfered with zoom/pan
    // User can close the detail panel with the X button instead
    
  }, [data, currentLevel, expandedClusters, phasedOutClusters, fadedEdgeOpacity, selectedCluster, focusedOutcome, handleClusterClick, handleClusterRightClick, dimensions, clusterLookup, clusterPositions, pinnedTooltip, contentScale]);
  
  useEffect(() => {
    renderGraph();
  }, [renderGraph]);
  
  // Reset zoom function
  const resetZoom = useCallback(() => {
    if (svgRef.current && zoomRef.current) {
      transformRef.current = d3.zoomIdentity; // Reset stored transform
      d3.select(svgRef.current)
        .transition()
        .duration(500)
        .call(zoomRef.current.transform, d3.zoomIdentity);
    }
  }, []);
  
  // Get selected cluster's children for the detail panel
  const selectedClusterData = selectedCluster ? clusterLookup[selectedCluster.cluster_id] : null;
  
  // Compute ranked attached edges for the selected cluster
  const rankedAttachedEdges = React.useMemo(() => {
    if (!selectedCluster || !data?.clusterEdges?.cluster_edges) return [];
    
    const selectedId = selectedCluster.cluster_id;
    const expandedSet = new Set(expandedClusters);
    
    // Replicate the same visibility logic from findEdgesForExpandedView
    const isVisible = (clusterId) => {
      const cluster = clusterLookup[clusterId];
      if (!cluster) return false;
      if (cluster.level === 0) return false;
      if (cluster.level <= currentLevel) return true;
      if (cluster.parent) {
        const parentId = cluster.parent.cluster_id;
        return isVisible(parentId) && expandedSet.has(parentId);
      }
      return false;
    };
    
    const isValidEndpoint = (clusterId) => {
      if (!isVisible(clusterId)) return false;
      if (expandedSet.has(clusterId)) return false;
      return true;
    };
    
    // Build set of valid endpoints (same as in graph rendering)
    const validEndpoints = new Set();
    Object.keys(clusterLookup).forEach(clusterId => {
      if (isValidEndpoint(clusterId)) {
        validEndpoints.add(clusterId);
      }
    });
    
    // Get the selected cluster and all its currently-visible descendants
    const selectedFamily = new Set([selectedId]);
    const addVisibleDescendants = (clusterId) => {
      const cluster = clusterLookup[clusterId];
      if (cluster?.children && expandedSet.has(clusterId)) {
        cluster.children.forEach(child => {
          selectedFamily.add(child.cluster_id);
          addVisibleDescendants(child.cluster_id);
        });
      }
    };
    addVisibleDescendants(selectedId);
    
    // Helper to check if a cluster (or its ancestors) is phased out
    const isClusterFaded = (clusterId) => {
      let current = clusterLookup[clusterId];
      while (current) {
        if (phasedOutClusters.has(current.cluster_id)) return true;
        current = current.parent;
      }
      return false;
    };
    
    // Helper to check if edge connects to focused outcome (if focus is active)
    const isConnectedToFocus = (edge) => {
      if (!focusedOutcome) return true; // No focus = all visible
      
      const focusedCluster = clusterLookup[focusedOutcome];
      const focusedFamily = new Set([focusedOutcome]);
      
      // Add ancestors of focused
      let current = focusedCluster;
      while (current && current.parent) {
        focusedFamily.add(current.parent.cluster_id);
        current = current.parent;
      }
      
      // Add descendants of focused
      if (focusedCluster) {
        const addDesc = (node) => {
          if (node.children) {
            node.children.forEach(child => {
              focusedFamily.add(child.cluster_id);
              addDesc(child);
            });
          }
        };
        addDesc(focusedCluster);
      }
      
      return focusedFamily.has(edge.source_cluster) || focusedFamily.has(edge.target_cluster);
    };
    
    // Score an edge based on histograms
    const scoreEdge = (edge) => {
      const histograms = edge.histograms || {};
      
      // Clinical interpretation: [very_beneficial, beneficial, neutral, harmful, very_harmful, not_applicable]
      const clinicalCounts = histograms.clinical_interpretation || [0, 0, 0, 0, 0, 0];
      const clinicalWeights = [5, 4, 3, 2, 1, 0];
      const clinicalTotal = clinicalCounts.reduce((a, b) => a + b, 0) || 1;
      const clinicalScore = clinicalCounts.reduce((sum, count, i) => sum + count * clinicalWeights[i], 0) / clinicalTotal;
      
      // Evidence strength: [strong, moderate, weak]
      const evidenceCounts = histograms.evidence_strength || [0, 0, 0];
      const evidenceWeights = [3, 2, 1];
      const evidenceTotal = evidenceCounts.reduce((a, b) => a + b, 0) || 1;
      const evidenceScore = evidenceCounts.reduce((sum, count, i) => sum + count * evidenceWeights[i], 0) / evidenceTotal;
      
      // Accessibility (as proxy for simplicity): [very_accessible, accessible, moderate, limited, very_limited, not_applicable]
      const accessCounts = histograms.accessibility || [0, 0, 0, 0, 0, 0];
      const accessWeights = [5, 4, 3, 2, 1, 0];
      const accessTotal = accessCounts.reduce((a, b) => a + b, 0) || 1;
      const accessScore = accessCounts.reduce((sum, count, i) => sum + count * accessWeights[i], 0) / accessTotal;
      
      // Combined score: 5*clinical + evidence + accessibility
      return 5 * clinicalScore + evidenceScore + accessScore;
    };
    
    // Find edges that are currently displayed AND connect to selected cluster
    const attachedEdges = data.clusterEdges.cluster_edges.filter(edge => {
      // Both endpoints must be valid (visible and not expanded)
      if (!validEndpoints.has(edge.source_cluster) || !validEndpoints.has(edge.target_cluster)) {
        return false;
      }
      
      // At least one endpoint must be in the selected family
      const connectsToSelected = selectedFamily.has(edge.source_cluster) || selectedFamily.has(edge.target_cluster);
      if (!connectsToSelected) return false;
      
      // Check if edge is faded (either endpoint is phased out)
      if (isClusterFaded(edge.source_cluster) || isClusterFaded(edge.target_cluster)) return false;
      
      // Check if edge connects to focus (if focus is active)
      if (!isConnectedToFocus(edge)) return false;
      
      return true;
    });
    
    // Score and sort edges
    const scoredEdges = attachedEdges.map(edge => ({
      ...edge,
      score: scoreEdge(edge),
      // Determine the "other" cluster (the one that's not in the selected family)
      otherCluster: selectedFamily.has(edge.source_cluster) ? edge.target_cluster : edge.source_cluster,
      otherName: selectedFamily.has(edge.source_cluster) ? edge.target_name : edge.source_name,
      direction: selectedFamily.has(edge.source_cluster) ? 'outgoing' : 'incoming'
    }));
    
    // Sort by score descending, take top 10
    scoredEdges.sort((a, b) => b.score - a.score);
    return scoredEdges.slice(0, 10);
  }, [selectedCluster, data, clusterLookup, phasedOutClusters, focusedOutcome, expandedClusters, currentLevel]);
  
  return (
    <div 
      className={`graph-container ${isFullscreen ? 'fullscreen' : ''}`}
      ref={containerRef}
    >
      {/* Controls - hidden in fullscreen */}
      {!isFullscreen && (
        <div className="controls">
          <button className="control-btn" onClick={resetZoom}>
            Reset Zoom
          </button>
          
          {focusedOutcome && (
            <button 
              className="control-btn clear-focus"
              onClick={() => setFocusedOutcome(null)}
            >
              Clear Focus
            </button>
          )}
          
          {phasedOutClusters.size > 0 && (
            <button 
              className="control-btn"
              onClick={() => setPhasedOutClusters(new Set())}
            >
              Show All ({phasedOutClusters.size} hidden)
            </button>
          )}
          
          {(phasedOutClusters.size > 0 || focusedOutcome) && (
            <div className="control-group">
              <label style={{ fontWeight: 'normal' }}>Faded edges:</label>
              <input
                type="range"
                min="0"
                max="0.2"
                step="0.01"
                value={fadedEdgeOpacity}
                onChange={(e) => setFadedEdgeOpacity(parseFloat(e.target.value))}
                style={{ width: '80px' }}
              />
              <span style={{ fontSize: '12px', color: '#6b7280', minWidth: '45px' }}>
                {fadedEdgeOpacity === 0 ? 'hidden' : `${Math.round(fadedEdgeOpacity * 100)}%`}
              </span>
            </div>
          )}
          
          {expandedClusters.size > 0 && (
            <button 
              className="control-btn"
              onClick={() => setExpandedClusters(new Set())}
            >
              Collapse All
            </button>
          )}
          
          {Object.keys(clusterPositions).length > 0 && (
            <button 
              className="control-btn"
              onClick={() => setClusterPositions({})}
            >
              Reset Positions
            </button>
          )}
          
          {(contentScale !== 1.0 || boxScale !== 1.0) && (
            <div className="control-group">
              {contentScale !== 1.0 && (
                <>
                  <span style={{ fontSize: '12px', color: '#6b7280' }}>
                    Graph: {Math.round(contentScale * 100)}%
                  </span>
                  <button 
                    className="control-btn"
                    onClick={() => setContentScale(1.0)}
                  >
                    Reset
                  </button>
                </>
              )}
              {boxScale !== 1.0 && (
                <>
                  <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: contentScale !== 1.0 ? '10px' : '0' }}>
                    Boxes: {Math.round(boxScale * 100)}%
                  </span>
                  <button 
                    className="control-btn"
                    onClick={() => setBoxScale(1.0)}
                  >
                    Reset
                  </button>
                </>
              )}
            </div>
          )}
          
          <button className="control-btn fullscreen-btn" onClick={toggleFullscreen}>
            Fullscreen
          </button>
          
          <div className="info">
            <p>Blue = Interventions (left) → Yellow = Outcomes (right)</p>
            <p>Click to select/expand | Right-click to hide | Ctrl+scroll to resize (graph or boxes)</p>
          </div>
        </div>
      )}
      
      {/* Fullscreen exit button */}
      {isFullscreen && (
        <button className="fullscreen-exit-btn" onClick={toggleFullscreen}>
          X Exit Fullscreen (Esc)
        </button>
      )}
      
      {/* SVG Canvas */}
      <svg 
        ref={svgRef} 
        width={dimensions.width} 
        height={dimensions.height}
        style={{ 
          border: isFullscreen ? 'none' : '1px solid #e2e8f0', 
          borderRadius: isFullscreen ? '0' : '8px', 
          background: '#fafafa' 
        }}
      />
      
      {/* Collapsible Legend - hidden in fullscreen */}
      {!isFullscreen && (
        <div className="legend-container">
          <button 
            className="legend-toggle"
            onClick={() => setShowLegend(!showLegend)}
          >
            {showLegend ? '▼ Hide Legend' : '▶ Show Legend'}
          </button>
          
          {showLegend && (
            <div className="legend-content">
              <div className="legend-section">
                <strong>Edge Color = Clinical Effect</strong>
                <div className="legend-items">
                  <div className="legend-item">
                    <span className="legend-line" style={{ backgroundColor: '#15803d' }}></span>
                    <span>Very Beneficial</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-line" style={{ backgroundColor: '#22c55e' }}></span>
                    <span>Beneficial</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-line" style={{ backgroundColor: '#94a3b8' }}></span>
                    <span>Neutral</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-line" style={{ backgroundColor: '#f97316' }}></span>
                    <span>Harmful</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-line" style={{ backgroundColor: '#dc2626' }}></span>
                    <span>Very Harmful</span>
                  </div>
                </div>
              </div>
              <div className="legend-section">
                <strong>Node Colors</strong>
                <div className="legend-items">
                  <div className="legend-item">
                    <span className="legend-circle" style={{ backgroundColor: '#dbeafe', borderColor: '#3b82f6' }}></span>
                    <span>Interventions / Factors</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-circle" style={{ backgroundColor: '#fef3c7', borderColor: '#f59e0b' }}></span>
                    <span>Outcomes</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Tooltip */}
      {tooltip && (
        <div 
          className="tooltip"
          style={{
            left: tooltip.x,
            top: tooltip.y,
            transform: `translate(10px, 10px) scale(${boxScale})`,
            transformOrigin: 'top left'
          }}
        >
          {tooltip.content.isChildTooltip ? (
            // Child circle tooltip
            <>
              <div className="tooltip-header">
                {tooltip.content.name}
              </div>
              <div className="tooltip-body">
                <p>{tooltip.content.description}</p>
                <p><strong>Contains:</strong> {tooltip.content.memberCount} concepts</p>
                {tooltip.content.hasChildren && (
                  <p className="tooltip-hint">Click to expand subcategories</p>
                )}
              </div>
            </>
          ) : (
            // Edge tooltip
            <>
              <div className="tooltip-header">
                {tooltip.content.source_name} &rarr; {tooltip.content.target_name}
              </div>
              <div className="tooltip-body">
                <p className="tooltip-stats">
                  <span><strong>Studies:</strong> {tooltip.content.num_edges}</span>
                  <span className="tooltip-separator">&bull;</span>
                  <span><strong>Effect:</strong> {tooltip.content.clinicalInterpretation?.replace(/_/g, ' ')}</span>
                </p>
                <p className="tooltip-description">{tooltip.content.description}</p>
                
                {tooltip.content.histograms && (
                  <div className="tooltip-histograms">
                    <HistogramBar 
                      label="Individual studies" 
                      counts={tooltip.content.histograms.clinical_interpretation}
                      colors={['#15803d', '#22c55e', '#94a3b8', '#f97316', '#dc2626', '#cbd5e1']}
                    />
                    <ConfidenceBar 
                      label="Average confidence" 
                      counts={tooltip.content.histograms.evidence_strength}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
      
      {/* Pinned Tooltip (stays until closed) */}
      {pinnedTooltip && (
        <div 
          className="tooltip pinned"
          style={{
            left: pinnedTooltip.x,
            top: pinnedTooltip.y,
            transform: `translate(10px, 10px) scale(${boxScale})`,
            transformOrigin: 'top left'
          }}
        >
          <button 
            className="tooltip-close-btn"
            onClick={() => setPinnedTooltip(null)}
          >
            ×
          </button>
          {pinnedTooltip.content.isChildTooltip ? (
            // Child circle tooltip
            <>
              <div 
                className="tooltip-header draggable"
                onMouseDown={handleTooltipDragStart}
              >
                {pinnedTooltip.content.name}
              </div>
              <div className="tooltip-body">
                <p>{pinnedTooltip.content.description}</p>
                <p><strong>Contains:</strong> {pinnedTooltip.content.memberCount} concepts</p>
                {pinnedTooltip.content.hasChildren && (
                  <p className="tooltip-hint">Click to expand subcategories</p>
                )}
              </div>
            </>
          ) : (
            // Edge tooltip
            <>
              <div 
                className="tooltip-header draggable"
                onMouseDown={handleTooltipDragStart}
              >
                {pinnedTooltip.content.source_name} &rarr; {pinnedTooltip.content.target_name}
              </div>
              <div className="tooltip-body">
                <p className="tooltip-stats">
                  <span><strong>Studies:</strong> {pinnedTooltip.content.num_edges}</span>
                  <span className="tooltip-separator">&bull;</span>
                  <span><strong>Effect:</strong> {pinnedTooltip.content.clinicalInterpretation?.replace(/_/g, ' ')}</span>
                </p>
                <p className="tooltip-description">{pinnedTooltip.content.description}</p>
                
                {pinnedTooltip.content.histograms && (
                  <div className="tooltip-histograms">
                    <HistogramBar 
                      label="Individual studies" 
                      counts={pinnedTooltip.content.histograms.clinical_interpretation}
                      colors={['#15803d', '#22c55e', '#94a3b8', '#f97316', '#dc2626', '#cbd5e1']}
                    />
                    <ConfidenceBar 
                      label="Average confidence" 
                      counts={pinnedTooltip.content.histograms.evidence_strength}
                    />
                  </div>
                )}
                
                {/* Collapsible Papers Section */}
                {pinnedTooltip.content.underlying_unified_edges && (
                  <PapersSection 
                    clusterEdge={pinnedTooltip.content}
                    unifiedEdgesMap={data?.unifiedEdgesMap}
                    papersMap={data?.papersMap}
                    isExpanded={showPinnedPapers}
                    onToggle={() => setShowPinnedPapers(!showPinnedPapers)}
                  />
                )}
              </div>
            </>
          )}
        </div>
      )}
      
      {/* Selected Cluster Panel */}
      {selectedCluster && selectedClusterData && (
        <div 
          className="detail-panel"
          style={{
            ...(detailPanelPosition ? {
              position: 'fixed',
              top: detailPanelPosition.y,
              left: detailPanelPosition.x,
              right: 'auto'
            } : {}),
            transform: `scale(${boxScale})`,
            transformOrigin: 'top right'
          }}
        >
          <div 
            className="detail-panel-header draggable"
            onMouseDown={handleDetailPanelDragStart}
          >
            <h3>{selectedClusterData.name}</h3>
            <button className="close-btn" onClick={() => setSelectedCluster(null)}>×</button>
          </div>
          <div className="detail-panel-body">
            <p className="cluster-description">{selectedClusterData.description}</p>
            
            {selectedCluster.isOutcome && (
              <div className="focus-section">
                {focusedOutcome === selectedCluster.cluster_id ? (
                  <button 
                    className="focus-btn active"
                    onClick={() => handleFocusOutcome(selectedCluster.cluster_id)}
                  >
                    Focused - Click to clear
                  </button>
                ) : (
                  <button 
                    className="focus-btn"
                    onClick={() => handleFocusOutcome(selectedCluster.cluster_id)}
                  >
                    Focus on this outcome
                  </button>
                )}
              </div>
            )}
          
          {/* Collapsible Additional Info */}
          <div className="additional-info-section">
            <button 
              className="additional-info-toggle"
              onClick={() => setShowDetailInfo(!showDetailInfo)}
            >
              {showDetailInfo ? '▼' : '▶'} Additional info
            </button>
            
            {showDetailInfo && (
              <div className="additional-info-content">
                <p><strong>Members:</strong> {getPrimaryMemberCount(selectedClusterData)} concepts</p>
                <p><strong>Type:</strong> {selectedCluster.isOutcome ? 'Outcome' : 'Intervention/Factor'}</p>
                
                {selectedClusterData.children && selectedClusterData.children.length > 0 && (
                  <div className="subclusters">
                    <strong>Subcategories:</strong>
                    <ol className="numbered-list">
                      {selectedClusterData.children.map((child, i) => (
                        <li key={child.cluster_id}>
                          <span className="subcluster-name">{child.name}</span>
                          <span className="subcluster-count">({getPrimaryMemberCount(child)})</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Collapsible Edges Attached */}
          <div className="additional-info-section">
            <button 
              className="additional-info-toggle"
              onClick={() => setShowEdgesInfo(!showEdgesInfo)}
            >
              {showEdgesInfo ? '▼' : '▶'} Edges attached ({rankedAttachedEdges.length})
            </button>
            
            {showEdgesInfo && (
              <div className="additional-info-content">
                {rankedAttachedEdges.length === 0 ? (
                  <p className="no-edges-message">No visible edges connected to this cluster.</p>
                ) : (
                  <ol className="edges-list">
                    {rankedAttachedEdges.map((edge, i) => {
                      // Get dominant clinical interpretation for color
                      const clinicalLabel = getDominantClinicalInterpretation(edge.histograms);
                      const clinicalColor = CONFIG.clinicalColors[clinicalLabel] || '#94a3b8';
                      
                      return (
                        <li 
                          key={edge.cluster_edge_id} 
                          className="edge-item"
                          onMouseEnter={(event) => {
                            if (pinnedTooltip) return; // Don't show hover if one is pinned
                            setTooltip({
                              x: event.clientX,
                              y: event.clientY,
                              content: {
                                ...edge,
                                clinicalInterpretation: clinicalLabel
                              }
                            });
                          }}
                          onMouseMove={(event) => {
                            if (pinnedTooltip) return;
                            setTooltip(prev => prev ? {
                              ...prev,
                              x: event.clientX,
                              y: event.clientY
                            } : null);
                          }}
                          onMouseLeave={() => {
                            if (!pinnedTooltip) {
                              setTooltip(null);
                            }
                          }}
                          onClick={(event) => {
                            event.stopPropagation();
                            setPinnedTooltip({
                              x: event.clientX,
                              y: event.clientY,
                              content: {
                                ...edge,
                                clinicalInterpretation: clinicalLabel
                              }
                            });
                            setShowPinnedPapers(false); // Reset papers section
                            setTooltip(null);
                          }}
                        >
                          <span 
                            className="edge-indicator" 
                            style={{ backgroundColor: clinicalColor }}
                          />
                          <span className="edge-direction">
                            {edge.direction === 'outgoing' ? '→' : '←'}
                          </span>
                          <span className="edge-other-name">{edge.otherName}</span>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </div>
            )}
          </div>
          </div>
        </div>
      )}
    </div>
  );
}


// =============================================================================
// HELPER COMPONENTS
// =============================================================================

function HistogramBar({ label, counts, colors }) {
  if (!counts || counts.length === 0) return null;
  
  const total = counts.reduce((a, b) => a + b, 0);
  if (total === 0) return null;
  
  return (
    <div className="histogram-bar">
      <span className="histogram-label">{label}:</span>
      <div className="histogram-track">
        {counts.map((count, i) => {
          const pct = (count / total) * 100;
          if (pct < 1) return null;
          return (
            <div 
              key={i}
              className="histogram-segment"
              style={{ 
                width: `${pct}%`, 
                backgroundColor: colors[i] || '#999' 
              }}
            />
          );
        })}
      </div>
    </div>
  );
}


function ConfidenceBar({ label, counts }) {
  if (!counts || counts.length === 0) return null;
  
  const total = counts.reduce((a, b) => a + b, 0);
  if (total === 0) return null;
  
  const weights = [3, 2, 1];
  const weightedSum = counts.reduce((sum, count, i) => sum + count * (weights[i] || 1), 0);
  const maxPossible = total * 3;
  const confidenceRatio = weightedSum / maxPossible;
  
  const grayValue = Math.round(180 - (confidenceRatio - 0.33) * (150 / 0.67));
  const barColor = `rgb(${grayValue}, ${grayValue}, ${grayValue})`;
  
  let confidenceLabel;
  if (confidenceRatio > 0.8) confidenceLabel = 'High';
  else if (confidenceRatio > 0.55) confidenceLabel = 'Moderate';
  else confidenceLabel = 'Low';
  
  return (
    <div className="confidence-bar">
      <span className="histogram-label">{label}:</span>
      <div className="confidence-track">
        <div 
          className="confidence-fill"
          style={{ 
            width: `${confidenceRatio * 100}%`, 
            backgroundColor: barColor
          }}
        />
      </div>
      <span className="confidence-label">{confidenceLabel}</span>
    </div>
  );
}


function LoadingSpinner() {
  return (
    <div className="loading">
      <div className="spinner"></div>
      <p>Loading knowledge graph...</p>
    </div>
  );
}


function ErrorMessage({ message }) {
  return (
    <div className="error">
      <h2>Error Loading Data</h2>
      <p>{message}</p>
      <p>Make sure the JSON files are in the <code>public/data/</code> folder.</p>
    </div>
  );
}


function PapersSection({ clusterEdge, unifiedEdgesMap, papersMap, isExpanded, onToggle }) {
  const papers = React.useMemo(() => {
    if (!unifiedEdgesMap || !papersMap) return [];
    return getUnderlyingPapers(clusterEdge, unifiedEdgesMap, papersMap);
  }, [clusterEdge, unifiedEdgesMap, papersMap]);
  
  if (papers.length === 0) return null;
  
  return (
    <div className="papers-section">
      <button className="papers-toggle" onClick={onToggle}>
        {isExpanded ? '▼' : '▶'} Source papers ({papers.length})
      </button>
      
      {isExpanded && (
        <ul className="papers-list">
          {papers.map((paper, i) => (
            <li key={paper.id || i} className="paper-item">
              <a 
                href={paper.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="paper-link"
              >
                <span className="paper-title">
                  {paper.title.length > 80 ? paper.title.substring(0, 77) + '...' : paper.title}
                </span>
                {paper.year && <span className="paper-year">({paper.year})</span>}
                <span className="paper-external-icon">↗</span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// =============================================================================
// MAIN APP
// =============================================================================

function App() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    loadData()
      .then(result => {
        if (result) {
          setData(result);
        } else {
          setError('Failed to load data files');
        }
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, []);
  
  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  
  return (
    <div className="App">
      <header className="App-header">
        <h1>Knowledge Visualization: Knee Rehabilitation</h1>
        <p>Explore research findings on what affects knee recovery</p>
      </header>
      
      <main>
        <KnowledgeGraph data={data} />
      </main>
    </div>
  );
}

export default App;
